import type { HealthMetricMap, IsoDate } from "./types.js";
import { addDays, daysBetween } from "../../shared/dates.js";

/**
 * 手動補登的純邏輯。
 *
 * 與 `src/domain/` 的其他模組同一條鐵則：**不讀系統時間、不讀全域狀態**，
 * 「今天」與期程都由呼叫端傳入。
 */

/**
 * 計畫期間內、今天以前、沒有步數資料的日子，由早到晚。
 *
 * 存在的理由是「缺資料」與「那天沒做到」在畫面上分不出來——兩者都只會讓
 * 月曆上那一格的 Rank 低一級。時間拖越久越還原不了真相，所以要有人主動說。
 *
 * **缺步數與 0 步是不同的事**：前者是沒同步到，後者是那天真的沒走。判斷條件
 * 因此是「不是有限數」而不是 `!steps_count`——寫成後者的話，走了 0 步的那天
 * 會被當成漏填，而你補上去的還是 0，提示卻永遠不會消失。
 *
 * 今天本身不算漏填——一天還沒過完，這時候提示只會每天早上騷擾一次。
 */
export function missingStepsDays(
  metrics: HealthMetricMap,
  today: IsoDate,
  planStart: IsoDate,
  planEnd: IsoDate
): IsoDate[] {
  // 掃到今天的前一天為止，而且不超出計畫期間
  const last = today < planEnd ? addDays(today, -1) : planEnd;
  const span = daysBetween(planStart, last);
  if (span < 0) return [];

  const missing: IsoDate[] = [];
  for (let i = 0; i <= span; i++) {
    const date = addDays(planStart, i);
    if (!Number.isFinite(metrics[date]?.steps_count as number)) missing.push(date);
  }
  return missing;
}

/** 畫面上的體重都是小數第 1 位，送出去的值不該與看到的值不一樣。 */
function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * 淨體重 = 體重 × (1 − 體脂率 / 100)。
 *
 * **這是淨體重的定義本身，不是估算。** 與 BMR／TDEE 那種「估算值」不同類，
 * 所以不在 `energy.ts`，畫面上也不需要「參考值」的但書。
 *
 * 任一個缺就回 null——**沒有體脂就是算不出淨體重**，補 0 會產生一個看起來
 * 很合理的假數字，而且它會一路流進 28 日淨體重變化與徽章。
 */
export function leanFrom(weightKg: number | null, fatPercent: number | null): number | null {
  if (!Number.isFinite(weightKg as number) || !Number.isFinite(fatPercent as number)) return null;
  return round1((weightKg as number) * (1 - (fatPercent as number) / 100));
}

/**
 * BMI = 體重 ÷ 身高（公尺）平方。
 *
 * 身高由呼叫端傳入——它存在 localStorage 的 `EnergyProfile` 裡，而這裡不讀
 * 全域狀態（與「今天」由呼叫端傳入同一條理由）。
 */
export function bmiFrom(weightKg: number | null, heightCm: number): number | null {
  if (!Number.isFinite(weightKg as number) || !Number.isFinite(heightCm) || heightCm <= 0) {
    return null;
  }
  const metres = heightCm / 100;
  return round1((weightKg as number) / (metres * metres));
}
